import React from 'react';

function fmtRemaining(ms) {
  const sec = Math.max(0, Math.ceil(ms / 1000));
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${d} day(s), ${h} hour(s), and ${m} minute(s)`;
}

function prettyActionName(formKey) {
  if (formKey === 'timeout') return 'Timeout';
  if (formKey === 'mute') return 'Mute';
  return 'Ban';
}

function fmtRelative(ts) {
  if (!ts) return 'Unknown';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} minute(s) ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} hour(s) ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)} day(s) ago`;
  return new Date(ts).toLocaleString();
}

function FallbackIcon({ name }) {
  const letter = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div className="appeal-card-icon appeal-card-icon-fallback" aria-hidden="true">
      {letter}
    </div>
  );
}

function MetaPill({ children, tone = 'default' }) {
  return <span className={`appeal-pill appeal-pill-${tone}`}>{children}</span>;
}

function getCardState(item) {
  if (item.appealSubmitted) {
    return {
      label: 'Submitted',
      tone: 'info',
      helper: 'Under review by the moderation team',
    };
  }

  const waitMs = (item.actionTime || 0) + (item.appealDelay || 0) - Date.now();
  if (waitMs > 0) {
    return {
      label: 'Waiting',
      tone: 'warn',
      helper: `Available in ${fmtRemaining(waitMs)}`,
    };
  }

  return {
    label: 'Ready',
    tone: 'success',
    helper: 'Click to submit your appeal',
  };
}

function noticeTitle(type) {
  if (type === 'success') return 'Success';
  if (type === 'warn') return 'Hold up';
  if (type === 'info') return 'Heads up';
  return 'Error';
}

export default function MyAppeals() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [active, setActive] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const loadAppeals = React.useCallback(() => {
    setLoading(true);
    fetch('/api/appeals/my', { credentials: 'include' })
      .then((response) => response.json())
      .then((data) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    loadAppeals();
  }, [loadAppeals]);

  const onCardClick = (item) => {
    if (item.appealSubmitted) {
      setNotice({
        type: 'info',
        text: 'This appeal has already been submitted and is currently under review by the server moderation team.',
      });
      return;
    }

    const waitMs = (item.actionTime || 0) + (item.appealDelay || 0) - Date.now();
    if (waitMs > 0) {
      setNotice({
        type: 'warn',
        text: `The required wait time has not passed yet. Please wait ${fmtRemaining(waitMs)} and try again.`,
      });
      return;
    }

    setActive({
      ...item,
      answers: (item.questions || []).map(() => ''),
    });
  };

  const updateAnswer = (index, value) => {
    setActive((current) => ({
      ...current,
      answers: current.answers.map((answer, i) => (i === index ? value.slice(0, 1024) : answer)),
    }));
  };

  const submitForm = async () => {
    if (!active) return;

    for (const answer of active.answers) {
      if (!answer || answer.trim().length < 1 || answer.trim().length > 1024) {
        setNotice({ type: 'error', text: 'Please answer all questions with 1–1024 characters.' });
        return;
      }
    }

    setSubmitting(true);
    const response = await fetch('/api/appeals/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        appealId: active.id,
        answers: active.answers.map((answer) => answer.trim()),
      }),
    });
    setSubmitting(false);

    if (response.ok) {
      setActive(null);
      setNotice({
        type: 'success',
        text: `Your ${active.formLabel.toLowerCase()} has been submitted. A moderator will review it soon.`,
      });
      loadAppeals();
      return;
    }

    const data = await response.json().catch(() => ({}));
    if (data.error === 'wait_more' && data.ms) {
      setNotice({
        type: 'warn',
        text: `The required wait time has not passed yet. Please wait ${fmtRemaining(data.ms)} and try again.`,
      });
    } else if (data.error === 'already_submitted') {
      setNotice({ type: 'info', text: 'This appeal has already been submitted and is under review.' });
      loadAppeals();
    } else if (data.error === 'questions_changed') {
      setNotice({
        type: 'warn',
        text: 'This form was updated by the server staff. Please reopen the appeal and try again.',
      });
      setActive(null);
      loadAppeals();
    } else if (data.error === 'disabled') {
      setNotice({
        type: 'error',
        text: 'This appeal form is currently disabled by the server staff.',
      });
      setActive(null);
      loadAppeals();
    } else {
      setNotice({
        type: 'error',
        text: 'There was an error submitting your appeal. Please try again later.',
      });
    }
  };

  return (
    <div className="appeals-page">
      <div className="appeals-page-header card">
        <div>
          <h2 className="title appeals-page-title">My Appeals</h2>
          <p className="muted appeals-page-subtitle">
            View your active ban, timeout, and mute appeals. Each card shows its current status and any
            details you may need before submitting.
          </p>
        </div>
        {!loading && items.length > 0 && (
          <div className="appeals-page-summary">
            <span className="appeal-pill appeal-pill-default">{items.length} active item{items.length === 1 ? '' : 's'}</span>
          </div>
        )}
      </div>

      {notice && (
        <div className={`card appeal-notice appeal-notice-${notice.type}`}>
          <strong>{noticeTitle(notice.type)}:</strong> {notice.text}
        </div>
      )}

      {loading ? (
        <div className="card">Loading your appeals…</div>
      ) : items.length === 0 ? (
        <div className="card appeals-empty-state">
          <h3 className="title" style={{ marginTop: 0 }}>No appeals found</h3>
          <p className="muted">
            You do not have any active ban, timeout, or mute appeals tied to your account right now. This is normal.
          </p>
        </div>
      ) : (
        <div className="grid appeals-grid appeals-grid-enhanced">
          {items.map((item) => {
            const state = getCardState(item);
            return (
              <button
                key={item.id}
                className={`card appeal-card appeal-card-${state.tone}`}
                onClick={() => onCardClick(item)}
                aria-label={`Open ${item.formLabel} for ${item.guildName}`}
              >
                <div className="appeal-card-topline">
                  <MetaPill>{item.formLabel}</MetaPill>
                  <MetaPill tone={state.tone}>{state.label}</MetaPill>
                </div>

                <div className="appeal-card-main">
                  {item.guildIcon ? (
                    <img
                      src={item.guildIcon}
                      alt={`${item.guildName} server icon`}
                      width="104"
                      height="104"
                      className="appeal-card-icon"
                    />
                  ) : (
                    <FallbackIcon name={item.guildName} />
                  )}

                  <div className="appeal-card-content">
                    <div className="title appeal-card-title">{item.guildName}</div>

                    <div className="muted appeal-card-meta">
                      {prettyActionName(item.formKey)} recorded {fmtRelative(item.actionTime)}
                    </div>

                    <div className="appeal-card-reason">
                      <span className="appeal-card-label">Reason</span>
                      <div className="appeal-card-reason-text">{item.actionReason || 'No reason provided'}</div>
                    </div>

                    <div className="appeal-card-footer">
                      <span className="muted appeal-card-helper">{state.helper}</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {active && (
        <div className="appeal-modal-overlay">
          <div className="card appeal-modal-card">
            <div className="appeal-modal-header">
              <div>
                <div className="appeal-modal-pills">
                  <MetaPill>{active.formLabel}</MetaPill>
                  <MetaPill>{active.guildName}</MetaPill>
                </div>
                <h3 className="title appeal-modal-title">Submit your appeal</h3>
                <p className="muted appeal-modal-subtitle">
                  {prettyActionName(active.formKey)} recorded {fmtRelative(active.actionTime)}
                </p>
              </div>
            </div>

            <div className="card appeal-modal-info">
              <div className="appeal-modal-info-title">Moderation details</div>
              <div className="appeal-modal-info-body">
                <strong>Type:</strong> {active.formLabel}
                <br />
                <strong>Reason:</strong> {active.actionReason || 'No reason provided'}
              </div>
            </div>

            {active.questions.length === 0 ? (
              <div className="card" style={{ marginBottom: 12 }}>
                This form currently has no questions configured. Please contact the server staff.
              </div>
            ) : (
              <div className="appeal-question-list">
                {active.questions.map((question, index) => (
                  <div key={index} className="appeal-question-block">
                    <label className="title appeal-question-label">{question}</label>
                    <textarea
                      value={active.answers[index]}
                      onChange={(event) => updateAnswer(index, event.target.value)}
                      rows={4}
                      className="appeal-question-input"
                      maxLength={1024}
                    />
                    <div className="muted appeal-question-count">{active.answers[index]?.length || 0}/1024</div>
                  </div>
                ))}
              </div>
            )}

            <div className="appeal-modal-actions">
              <button
                className="button"
                onClick={() => setActive(null)}
                disabled={submitting}
                style={{ background: 'transparent', color: 'var(--fg)', border: '1px solid var(--border)' }}
              >
                Cancel
              </button>
              <button className="button" onClick={submitForm} disabled={submitting || active.questions.length === 0}>
                {submitting ? 'Submitting…' : 'Submit appeal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
